import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getOrCreatePushSubscription, savePushSubscription } from "@/lib/web-push";

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const supported =
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window;

    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error("[Push] check failed:", error);
    }
  };

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      toast.error("Push notifications not supported");
      return false;
    }
    setLoading(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === "granted") return true;
      if (result === "denied") toast.error("Notification permission denied");
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return null;
    if (permission !== "granted") {
      const granted = await requestPermission();
      if (!granted) return null;
    }

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;

      // Handles VAPID key rotation: stale subscriptions are re-created
      // under the current server key automatically.
      const subscription = await getOrCreatePushSubscription(registration);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const saved = await savePushSubscription(user.id, subscription);
        if (!saved) {
          toast.error("Failed to read subscription keys");
          return null;
        }
      }

      setIsSubscribed(true);
      toast.success("Push notifications enabled");
      return subscription;
    } catch (error) {
      console.error("[Push] subscribe failed:", error);
      toast.error("Failed to enable push notifications");
      return null;
    } finally {
      setLoading(false);
    }
  }, [isSupported, permission, requestPermission]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        const endpoint = subscription.endpoint;
        await subscription.unsubscribe();
        await supabase.from("push_subscriptions").delete().eq("endpoint", endpoint);
        setIsSubscribed(false);
        toast.success("Push notifications disabled");
      }
    } catch (error) {
      console.error("[Push] unsubscribe failed:", error);
      toast.error("Failed to disable push notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  const showNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (permission !== "granted") return;
    try {
      new Notification(title, { icon: "/logo.png", badge: "/logo.png", ...options });
    } catch (error) {
      console.error("[Push] showNotification failed:", error);
    }
  }, [permission]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    requestPermission,
    subscribe,
    unsubscribe,
    showNotification,
  };
}
